import { useEffect, useMemo, useState, useCallback } from "react";
import { useFilters, useSortBy, useTable } from "react-table";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { dispatch, getState } from "../../../../state/store";
import { setLocations } from "../../../../state/ducks";
import { selectLocations, selectMarketIso } from "../../../../state/selectors";

import { strCode } from "../../../../consts";
import { t } from "../../../../services";
import { objectUtil } from "../../../../utils";

import { FlexContainers, Paragraph2, Table, WrapperContainer } from "../../../../components";
import { StoresTableColumns } from "./StoresTableColumns";
import { StoresTableWrapper } from "./styled";

const StoresTable = ({
  storesData = [],
  searchValue,
  editId,
  setEditId = () => {},
  onSaveStore = () => {},
  onDeleteStore = () => {},
  isReadOnlyAccess
}) => {
  const data = useMemo(() => storesData, [storesData]);
  const columns = useMemo(() => StoresTableColumns, []);

  const loading = useSelector((store) => store.configs?.loading);
  const error = useSelector((store) => store.configs?.error);

  const [editStoreData, setEditStoreData] = useState({});
  const [editDisabled, setEditDisabled] = useState(false);
  const [editErrors, setEditErrors] = useState([]);

  const market = selectMarketIso()(getState());

  const tableInstance = useTable(
    {
      data,
      columns,
      storesData,
      initialState: {
        sortBy: [
          {
            id: "name",
            desc: false
          }
        ]
      }
    },
    useFilters,
    useSortBy
  );
  const { setFilter } = tableInstance;

  useEffect(() => {
    if (searchValue) {
      setFilter("name", searchValue);
    } else {
      setFilter("name", "");
    }
  }, [storesData, searchValue]);

  const validateStoreData = useCallback(() => {
    const originalData = storesData.find((store) => store.id === editId);

    const isSame = objectUtil.objEquals(originalData, editStoreData);
    const newStoreName = editStoreData?.name;

    const errorAttributes = [];

    const isEmpty = !newStoreName || !editStoreData?.city;

    if (newStoreName) {
      const isNotUnique = storesData.find(
        (store) => store.name.toLowerCase() === newStoreName.toLowerCase() && store.id !== editId
      );

      if (isNotUnique) {
        errorAttributes.push("name");
      }
    }

    setEditErrors(errorAttributes);
    setEditDisabled((editStoreData?.id !== -1 && isSame) || isEmpty);
  }, [storesData, editStoreData, editId]);

  useEffect(() => {
    validateStoreData();
  }, [editStoreData]);

  useEffect(() => {
    if (editId === -1) {
      const newStore = storesData?.find((store) => store.id === editId);

      setEditStoreData(newStore || {});
    }
  }, [editId, storesData]);

  const onEdit = (id) => {
    setEditId(id);

    const editedStore = storesData.find((store) => store.id === id) ?? {};
    setEditStoreData(editedStore);
  };

  const onEditChanges = (data) => {
    if (data?.key) {
      const newData = { ...editStoreData, [data.key]: data.value };
      setEditStoreData(newData);
    }
  };

  const onSaveChanges = async () => {
    try {
      await onSaveStore(editStoreData);
      setEditStoreData({});
      // eslint-disable-next-line no-empty
    } catch (err) {}
  };

  const onCancel = () => {
    const locationsByMarket = selectLocations(market)(getState());
    const filteredLocations = locationsByMarket?.filter((location) => location.id !== -1) || [];

    dispatch(setLocations({ market, locations: [...filteredLocations] }));

    setEditId(null);
    setEditStoreData({});
  };

  return (
    <WrapperContainer width={"100%"}>
      {loading ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnCenter}
          width={"100%"}
          height={200}
          textAlign={"center"}>
          <Paragraph2>{t(strCode.LOADING)}</Paragraph2>
        </WrapperContainer>
      ) : error ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnCenter}
          width={"100%"}
          height={200}
          textAlign={"center"}>
          <Paragraph2>{t(strCode.LOCATIONS_FETCH_ERROR)}</Paragraph2>
        </WrapperContainer>
      ) : (
        storesData && (
          <StoresTableWrapper>
            <Table
              tableInstance={tableInstance}
              maxTableHeight={"200px"}
              searchedText={searchValue}
              searchInfo={t(strCode.NO_STORES_FOUND, { searchValue })}
              searchCheckInfo={t(strCode.CHECK_STORE_MESSAGE)}
              noDataInfo={t(strCode.NO_STORES_DATA)}
              editDisabled={editDisabled}
              editErrors={editErrors}
              maxVisibleRows={7}
              editId={editId}
              callbackFn1={onEdit}
              callbackFn2={onDeleteStore}
              callbackFn3={onEditChanges}
              callbackFn4={onSaveChanges}
              callbackFn5={onCancel}
              callbackFn6={() => isReadOnlyAccess}
              showTableBorder
              modalTable
            />
          </StoresTableWrapper>
        )
      )}
    </WrapperContainer>
  );
};

StoresTable.propTypes = {
  storesData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      address: PropTypes.string,
      city: PropTypes.string.isRequired
    })
  ).isRequired,
  searchValue: PropTypes.string.isRequired,
  editId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setEditId: PropTypes.func.isRequired,
  onSaveStore: PropTypes.func.isRequired,
  onDeleteStore: PropTypes.func.isRequired,
  isReadOnlyAccess: PropTypes.bool.isRequired
};

export default StoresTable;
